import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Table,
	Upload,
	UploadFile,
} from 'antd';
import {
	DeleteOutlined,
	EditOutlined,
	FileSearchOutlined,
	PlusOutlined,
	UploadOutlined,
	LoadingOutlined,
} from '@ant-design/icons';
import {
	CustomerCompanyShareholderInformationForm,
	useFRMCustomerPage,
} from './CustomerPage';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useModals } from '@/components/modals/ModalProvider';
import { formItem, useForm } from '@/libs/antd/form';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import rule, { rules } from '@/libs/antd/rules';
import {
	useSelectAddress,
	useSelectCitizenship,
	useSelectCountry,
	useSelectIdentityType,
	useSelectShareholderType,
} from '@/libs/fleet-api/hooks/master-data';
import { ShareholderType } from '@/libs/fleet-api/enums';
import { inputKitas, inputKtp, inputPassport } from '@/libs/antd/input';
import { CustomerShareholder, FileData } from '@/libs/fleet-api/services/types';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import { inputRtRw } from '@/libs/antd/input-number';

const FRMCustomerLegalShareholder: FC<{ onSubmit: () => void }> = ({
	onSubmit,
}) => {
	const { modal, openShareholderModal } = useModals();
	const { customerForm, shareholderForm, totalShareholding, validationStatus } =
		useFRMCustomerPage();

	return (
		<Fragment>
			<Form.Item shouldUpdate>
				{({ getFieldValue }) => {
					const dataSource =
						(getFieldValue(
							customerForm.fieldName('customerCompanyShareholderInformation'),
						) as CustomerCompanyShareholderInformationForm[]) ?? [];

					return (
						<Fragment>
							<div style={{ textAlign: 'right', marginBottom: 10 }}>
								<Button
									type="link"
									icon={<PlusOutlined />}
									disabled={totalShareholding >= 100}
									onClick={() => {
										shareholderForm.setModalState({ type: 'create' });
									}}
								>
									Tambah Pemegang Saham
								</Button>
							</div>
							<Table
								pagination={false}
								bordered
								dataSource={dataSource.map((data, key) => {
									return { ...data, key };
								})}
								scroll={{ x: 2000 }}
								onRow={(data) => {
									const isNationalityEmpty =
										data.shareholderType.id === 1 &&
										(!data.citizenship || !data.nationality);

									if (
										isNationalityEmpty ||
										!data.name ||
										!data.identityCardType ||
										!data.identityFile ||
										!data.shareholderType ||
										!data.address ||
										!data.rt ||
										!data.rw ||
										!data.postal ||
										!data.shareholding
									) {
										return {
											style: {
												background: '#fff3f399',
											},
										};
									}

									return {};
								}}
								columns={[
									{
										title: 'Action',
										width: 1,
										align: 'center',
										colSpan: 3,
										render: (_, record) => (
											<a
												style={{ display: 'block' }}
												onClick={(e) => {
													e.preventDefault();
													openShareholderModal(
														record as unknown as CustomerShareholder,
													);
												}}
											>
												<FileSearchOutlined />
											</a>
										),
									},
									{
										width: 1,
										align: 'center',
										colSpan: 0,
										render: (_, record, index) => {
											if (record.isManagement) {
												return (
													<EditOutlined
														style={{ color: '#bababa' }}
														disabled
													/>
												);
											}
											return (
												<a
													style={{ display: 'block' }}
													onClick={(e) => {
														e.preventDefault();
														shareholderForm.setModalState({
															type: 'edit',
															index,
														});
														shareholderForm.form?.setFieldsValue(record);
													}}
												>
													<EditOutlined />
												</a>
											);
										},
									},
									{
										width: 1,
										align: 'center',
										colSpan: 0,
										render: (_, record, index) => {
											if (record.isManagement) {
												return (
													<DeleteOutlined
														style={{ color: '#bababa' }}
														disabled
													/>
												);
											}
											return (
												<a
													style={{ display: 'block' }}
													onClick={async (e) => {
														e.preventDefault();
														const isConfirmed = await modal.confirm({
															title: 'Hapus data pemegang saham?',
														});

														if (!isConfirmed) return;
														customerForm.form.setFieldValue(
															customerForm.fieldName(
																'customerCompanyShareholderInformation',
															),
															customerForm.value?.customerCompanyShareholderInformation?.filter(
																(_, dataIndex) => dataIndex !== index,
															),
														);
													}}
												>
													<DeleteOutlined />
												</a>
											);
										},
									},
									{
										title: 'Nama',
										dataIndex: ['name'],
									},
									// {
									// 	title: 'id',
									// 	dataIndex: ['id'],
									// },
									// {
									// 	title: 'tempId',
									// 	dataIndex: ['tempId'],
									// },
									{
										title: 'Tipe Identitas',
										dataIndex: ['shareholderType', 'value'],
									},
									{
										title: 'Kewarganegaraan',
										render: (_, record) =>
											record.shareholderType?.id === 2
												? '-'
												: record.citizenship?.value,
									},
									{
										title: 'Jenis Identitas',
										dataIndex: ['identityCardType', 'value'],
									},
									{
										title: 'No. ID',
										dataIndex: ['identityNumber'],
									},
									{
										title: 'Alamat Perusahaan',
										dataIndex: ['address'],
									},
									{
										title: 'Kode Pos',
										dataIndex: ['postal', 'postalCode'],
									},
									{
										title: 'Kota',
										dataIndex: ['postal', 'cityName'],
									},
									{
										title: 'Kecamatan',
										dataIndex: ['postal', 'districtName'],
									},
									{
										title: 'Kelurahan',
										dataIndex: ['postal', 'locationName'],
									},
									{
										title: 'RT/RW',
										render: (record) => `${record.rt}/${record.rw}`,
									},
									{
										title: 'No. Telepon',
										dataIndex: ['phoneNumber'],
									},
									{
										title: 'Kepemilikan Saham (%)',
										render: (record) => `${record.shareholding}%`,
									},
								]}
							/>
						</Fragment>
					);
				}}
			</Form.Item>

			<Button
				type="primary"
				style={{
					width: 200,
					display: 'block',
					marginLeft: 'auto',
					marginTop: 60,
				}}
				disabled={!validationStatus.legal || !validationStatus.shareholder}
				onClick={() => onSubmit()}
			>
				NEXT
			</Button>
		</Fragment>
	);
};

export default FRMCustomerLegalShareholder;

export const useFRMCustomerLegalShareholderForm = () => {
	const { form, value, fieldName } =
		useForm<CustomerCompanyShareholderInformationForm>();

	const [modalState, setModalState] = useState<
		{ type: 'create' } | { type: 'edit'; index: number } | null
	>(null);

	return {
		form,
		value,
		fieldName,
		modalState,
		setModalState,
	};
};

export const FRMCustomerLegalShareholderForm: FC<
	ReturnType<typeof useFRMCustomerLegalShareholderForm>
> = (props) => {
	const { form, value, fieldName, modalState, setModalState } = props;
	const { customerForm, customerDetail, isEdit, customerId } =
		useFRMCustomerPage();

	const selectShareholderType = useSelectShareholderType();
	const selectCitizenship = useSelectCitizenship();
	const selectCountry = useSelectCountry();
	const selectAddress = useSelectAddress();
	const selectIdentityCardType = useSelectIdentityType(
		value?.shareholderType?.id,
	);

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<Form
			{...ANTD_DEFAULT_PROPS.FORM}
			form={form}
			labelCol={{ span: 10 }}
			wrapperCol={{ span: 14 }}
			onFinish={(value) => {
				const existings =
					customerForm.value?.customerCompanyShareholderInformation ?? [];

				// Calculate total shareholding including current input
				let totalShareholding = existings.reduce((acc, shareholder) => {
					return acc + shareholder.shareholding;
				}, 0);

				// For edit mode, subtract the old value before adding the new one
				if (modalState?.type === 'edit') {
					const oldValue = existings[modalState.index]?.shareholding || 0;
					totalShareholding = totalShareholding - oldValue + value.shareholding;
				} else {
					totalShareholding = totalShareholding + value.shareholding;
				}

				// Round to 2 decimal places to avoid floating-point precision issues
				totalShareholding = Number(totalShareholding.toFixed(2));

				// Validate total shareholding doesn't exceed 100%
				if (totalShareholding > 100) {
					throw new Error('Total shareholding cannot exceed 100%');
				}

				const updated =
					modalState?.type === 'edit'
						? existings.map((doc, docIndex) =>
								modalState.index === docIndex ? value : doc,
							)
						: [...existings, value];

				customerForm.form.setFieldValue(
					customerForm.fieldName('customerCompanyShareholderInformation'),
					updated,
				);

				setModalState(null);
			}}
		>
			<FleetModal
				open={Boolean(modalState)}
				width={940}
				closeIcon={false}
				keyboard={false}
				maskClosable={false}
				buttonPosition="end"
				okButtonProps={{
					disabled: uploadMutation.isPending,
				}}
				onOk={() => form?.submit()}
				onCancel={() => setModalState(null)}
				afterClose={() => form.resetFields()}
			>
				<FleetTitle>Tambah Pemegang Saham</FleetTitle>
				<Form.Item
					name={fieldName('id')}
					noStyle
				/>
				<Form.Item
					name={fieldName('tempId')}
					noStyle
				/>
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('name')}
							label="Nama"
							rules={
								isEdit && customerDetail?.customerInformation.source === 'Estar'
									? [rule.required()]
									: [rule.required(), rule.charOnly()]
							}
						>
							<Input />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('shareholderType')}
							label="Tipe Pemegang Saham"
							rules={[rule.required()]}
							getValueFromEvent={(_, option) => option?.data}
							getValueProps={(value) => {
								if (!value) return {};
								return {
									value: {
										value: value.id,
										label: value.value,
									},
								};
							}}
						>
							<Select
								{...selectShareholderType}
								onSelect={(value, option) => {
									selectShareholderType.onSelect!(value, option);
									form.resetFields([
										fieldName('identityCardType'),
										fieldName('identityNumber'),
									]);
								}}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				{value?.shareholderType?.id === ShareholderType.Perorangan && (
					<Fragment>
						<Row gutter={[40, 20]}>
							<Col span={12}>
								<Form.Item
									name={fieldName('citizenship')}
									label="Kewarganegaraan"
									rules={[rule.required()]}
									getValueFromEvent={(_, option) => option?.data}
									getValueProps={(value) => {
										if (!value) return {};
										return {
											value: {
												value: value.id,
												label: value.value,
											},
										};
									}}
								>
									<Select
										{...selectCitizenship}
										onSelect={(value, option) => {
											selectCitizenship.onSelect!(value, option);
											form.resetFields([
												fieldName('nationality'),
												fieldName('identityCardType'),
											]);
										}}
									/>
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item
									name={fieldName('nationality')}
									label="Nationality"
									rules={[rule.required()]}
									getValueFromEvent={(_, option) => option?.data}
									getValueProps={(value) => {
										if (!value) return {};
										return {
											value: {
												value: value.id,
												label: value.name,
											},
										};
									}}
								>
									<Select
										{...selectCountry}
										options={selectCountry.options?.filter((option) => {
											return value?.citizenship?.id === 1
												? option.value === 1
												: option.value !== 1;
										})}
									/>
								</Form.Item>
							</Col>
						</Row>
						<Divider />
					</Fragment>
				)}
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('identityCardType')}
							label="Jenis Identitas"
							rules={[rule.required()]}
							getValueFromEvent={(_, option) => option?.data}
							getValueProps={(value) => {
								if (!value) return {};
								return {
									value: {
										value: value.id,
										label: value.value,
									},
								};
							}}
						>
							<Select
								{...selectIdentityCardType}
								options={
									value?.citizenship?.id === 2
										? selectIdentityCardType.options?.filter(
												(option) => option.value !== 1,
											)
										: selectIdentityCardType.options
								}
								onSelect={(value, option) => {
									selectIdentityCardType.onSelect!(value, option);
									form.resetFields([fieldName('identityNumber')]);
								}}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						{value?.shareholderType?.id === ShareholderType.Perorangan ? (
							<Fragment>
								{value?.identityCardType?.id === 1 && (
									<Form.Item
										name={fieldName('identityNumber')}
										label="No. ID"
										rules={rules.ktp}
									>
										<Input {...inputKtp} />
									</Form.Item>
								)}
								{value?.identityCardType?.id === 2 && (
									<Form.Item
										name={fieldName('identityNumber')}
										label="No. ID"
										rules={rules.passport}
									>
										<Input {...inputPassport} />
									</Form.Item>
								)}
								{value?.identityCardType?.id === 3 && (
									<Form.Item
										name={fieldName('identityNumber')}
										label="No. ID"
										rules={rules.kitas}
									>
										<Input {...inputKitas} />
									</Form.Item>
								)}
							</Fragment>
						) : (
							<Form.Item
								name={fieldName('identityNumber')}
								label="No. NPWP"
								{...formItem.npwp}
							/>
						)}
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Upload Identitas"
							name={fieldName('identityFile')}
							rules={[rule.required()]}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name,
													thumbUrl: file.url,
												},
											] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
						>
							<Upload.Dragger
								listType="picture"
								accept=".pdf,.jpg,.jpeg,.png"
								maxCount={1}
								beforeUpload={() => false}
								onRemove={() => {
									form.setFieldValue(fieldName('identityFile'), null);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = customerId
										? `customer/${customerId}/shareholder-document`
										: 'customer/temp/shareholder-document';

									try {
										const url = await uploadMutation.mutateAsync({
											file: file as unknown as File,
											fileid,
											filepath,
										});

										form.setFieldValue(fieldName('identityFile'), {
											format: 'jpeg',
											id: fileid,
											name: file.name,
											path: filepath,
											url,
										});
									} catch {
										form.setFieldValue(fieldName('identityFile'), null);
									}
								}}
							>
								{uploadMutation.isPending ? (
									<LoadingOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								) : (
									<UploadOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								)}
							</Upload.Dragger>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('address')}
							label="Alamat"
							rules={[rule.required()]}
						>
							<Input.TextArea rows={3} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="RT/RW"
							required
						>
							<Row
								wrap={false}
								align="middle"
								gutter={8}
							>
								<Col flex="auto">
									<Form.Item
										name={fieldName('rt')}
										noStyle
										rules={rules.rt}
									>
										<Input {...inputRtRw} />
									</Form.Item>
								</Col>
								<Col flex="none">/</Col>
								<Col flex="auto">
									<Form.Item
										name={fieldName('rw')}
										noStyle
										rules={rules.rw}
									>
										<Input {...inputRtRw} />
									</Form.Item>
								</Col>
							</Row>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('postal')}
							label="Kode POS"
							rules={[rule.required()]}
							getValueFromEvent={(_, option) => option?.data}
							getValueProps={(value) => {
								if (!value) return {};
								return {
									value: {
										value: value.postalId,
										label: value.postalCode,
									},
								};
							}}
						>
							<Select {...selectAddress} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Kelurahan"
							shouldUpdate
						>
							<Input
								disabled
								value={value?.postal?.locationName}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Kecamatan"
							shouldUpdate
						>
							<Input
								disabled
								value={value?.postal?.districtName}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Kota"
							shouldUpdate
						>
							<Input
								disabled
								value={value?.postal?.cityName}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('phoneNumber')}
							label="No. Telepon"
							validateTrigger={['onBlur', 'onFocus']}
							rules={[rule.strNumber(), rule.phoneNumber()]}
						>
							<Input maxLength={13} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('shareholding')}
							label="Kepemilikan Saham"
							rules={[rule.required()]}
						>
							<InputNumber
								min={0}
								max={100}
								suffix="%"
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
			</FleetModal>
		</Form>
	);
};
